import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentThread } from '@dreamai/types';
import { useAgent } from '../../../contexts/AgentContext';
import { useThreadReads } from '../../../providers/ThreadReadsProvider';
import api from '../../../api';
import { ReactComponent as PinIcon } from '../../../icons/pin.svg';
import { ReactComponent as SendIcon } from '../../../icons/send.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/campaign.svg';
import { ReactComponent as ClipboardIcon } from '../../../icons/clipboard-list.svg';
import { ReactComponent as MentionAtIcon } from '../../../icons/mention-at.svg';
import './side-nav-section.scss';

interface GetPinnedThreadsResponse {
  success: boolean;
  threads: AgentThread[];
  pinnedThreadIds: string[];
  error?: string;
}

export interface PinnedThreadsSectionRef {
  refreshPinnedThreads: () => void;
}

const PinnedThreadsSection = forwardRef<PinnedThreadsSectionRef, object>(
  (_, ref) => {
    const { getAccessTokenSilently } = useAuth0();
    const { getAgentById } = useAgent();
    const [searchParams] = useSearchParams();
    const { getUnreadThreadIdsByAgent } = useThreadReads();

    const currentThreadId = searchParams.get('thread_id');

    const [threads, setThreads] = useState<AgentThread[]>([]);
    const [initialLoading, setInitialLoading] = useState(true);

    // Get all unread thread IDs
    const allUnreadThreadIds = useMemo(() => {
      const allIds = new Set<string>();
      // We need to check across all agents
      return allIds;
    }, []);

    const fetchPinnedThreads = async () => {
      try {
        // Don't set loading on refresh to avoid flicker
        const token = await getAccessTokenSilently();
        const response = await api.get<GetPinnedThreadsResponse>(
          '/agents/threads/pinned',
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.success) {
          setThreads(response.data.threads);
        }
      } catch (err) {
        console.error('Error fetching pinned threads:', err);
      } finally {
        setInitialLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      refreshPinnedThreads: fetchPinnedThreads,
    }));

    useEffect(() => {
      fetchPinnedThreads();
    }, [getAccessTokenSilently]);

    const getThreadIcon = (thread: AgentThread) => {
      let icon = <SendIcon />;
      if (
        thread.discussing_objects?.some(
          (d) => d.discussing_object === 'Campaign',
        )
      ) {
        icon = <CampaignIcon />;
      } else if (
        thread.discussing_objects?.some((d) => d.discussing_object === 'Plan')
      ) {
        icon = <ClipboardIcon />;
      }

      if (thread.has_unread_mentions) {
        return <MentionAtIcon />;
      }

      if (thread.has_pending_confirmations) {
        return (
          <div className='thread-icon-wrapper'>
            {icon}
            <div className='action-required-indicator'>
              <div className='action-required-indicator-dot' />
            </div>
          </div>
        );
      }

      return icon;
    };

    const getThreadName = (thread: AgentThread) => {
      if (thread.is_main_thread) return 'Direct Message';
      return thread.description || 'Untitled';
    };

    const getThreadClasses = (thread: AgentThread) => {
      const classes = ['thread-item', 'pinned'];
      if (currentThreadId === thread.id) classes.push('active');
      // TODO: Check unread status from provider
      if (thread.has_pending_confirmations)
        classes.push('has-pending-confirmations');
      if (thread.has_unread_mentions) classes.push('has-unread-mentions');
      return classes.join(' ');
    };

    // Don't render if no pinned threads (after initial load)
    if (!initialLoading && threads.length === 0) {
      return null;
    }

    return (
      <div className='side-nav-section pinned-section'>
        <div className='section-header'>
          <div className='agent-info'>
            <div className='pinned-icon'>
              <PinIcon />
            </div>
            <p className='nickname'>Pinned</p>
          </div>
        </div>

        <div className='threads'>
          {threads.map((thread) => {
            const agent = thread.agent_id
              ? getAgentById(thread.agent_id)
              : null;
            return (
              <Link
                key={thread.id}
                to={`/conversations?agent_id=${thread.agent_id || ''}&thread_id=${thread.id}`}
                className={getThreadClasses(thread)}
              >
                {getThreadIcon(thread)}
                <span className='thread-name'>{getThreadName(thread)}</span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  },
);

PinnedThreadsSection.displayName = 'PinnedThreadsSection';

export default PinnedThreadsSection;
